import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Star, Clock, Award, ArrowRight, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import PhotoGallery from '../components/PhotoGallery';
import BeforeAfterSlider from '../components/BeforeAfterSlider';

const HomePage = () => {
  const features = [
    {
      icon: <Shield className="h-8 w-8 text-red-600" />,
      title: "Premium Quality",
      description: "High-grade window films that provide superior protection and durability."
    },
    {
      icon: <Star className="h-8 w-8 text-red-600" />,
      title: "Expert Installation",
      description: "Professional technicians with years of experience in window tinting."
    },
    {
      icon: <Clock className="h-8 w-8 text-red-600" />,
      title: "Quick Service",
      description: "Fast, efficient installation that respects your time and schedule."
    },
    {
      icon: <Award className="h-8 w-8 text-red-600" />,
      title: "Warranty Included",
      description: "Comprehensive warranty coverage for your peace of mind."
    }
  ];

  const benefits = [
    "UV Protection - Block up to 99% of harmful UV rays",
    "Heat Reduction - Keep your car and home cooler",
    "Privacy Enhancement - Increased privacy and security",
    "Glare Reduction - Reduced eye strain and improved comfort",
    "Energy Savings - Lower cooling costs for residential tinting",
    "Fade Protection - Protect interior furnishings from sun damage"
  ];

  // Before/After pairs using existing gallery images
  const beforeAfterPairs = [
    {
      before: '/images/gallery/IMG_2062.png',
      after: '/images/gallery/IMG_2063.jpeg',
      alt: 'Automotive Tinting'
    },
    {
      before: '/images/gallery/IMG_2066.jpeg',
      after: '/images/gallery/IMG_2067.jpeg',
      alt: 'Vehicle Window Tint'
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 md:py-40 overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/gallery/IMG_2063.jpeg')" }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6 sm:space-y-8">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white leading-tight drop-shadow-lg">
              Professional <span className="text-red-500">Window Tinting</span>
              <br className="hidden sm:block" />
              <span className="sm:hidden"> </span>in Missouri City, TX
            </h1>

            <p className="text-lg sm:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              Transform your vehicle and home with premium window tinting services.
              Expert installation, quality materials, and unbeatable results guaranteed.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 sm:pt-6">
              <Button asChild size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 w-full sm:w-auto">
                <Link to="/book">Book Appointment</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg rounded-lg transition-all duration-200 w-full sm:w-auto">
                <Link to="/services">View Services</Link>
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-gray-300 pt-4 sm:pt-8">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Licensed & Insured</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Lifetime Warranty</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Free Estimates</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Texas Tint Studio?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Quality materials, expert craftsmanship, and exceptional service.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-4 sm:p-8 text-center space-y-3 sm:space-y-4">
                  <div className="flex justify-center">{feature.icon}</div>
                  <h3 className="text-base sm:text-xl font-semibold text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base hidden sm:block">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See the Difference
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Drag the slider to compare before and after our professional tinting service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {beforeAfterPairs.map((pair, index) => (
              <BeforeAfterSlider
                key={index}
                beforeSrc={pair.before}
                afterSrc={pair.after}
                alt={pair.alt}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <PhotoGallery />

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Benefits of Professional Window Tinting
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  Window tinting provides real benefits that improve comfort, protection, and energy efficiency.
                </p>
              </div>

              <div className="space-y-3 sm:space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{benefit}</p>
                  </div>
                ))}
              </div>

              <Button asChild className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg transition-all duration-200 hover:shadow-lg inline-flex items-center space-x-2">
                <Link to="/services">
                  <span>Learn More About Our Services</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl">
                <div className="text-center space-y-4 sm:space-y-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
                    <Shield className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Premium Protection</h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                    Our high-quality window films provide superior UV protection, heat rejection, and privacy enhancement.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                    <p className="text-3xl sm:text-4xl font-bold text-red-600 mb-2">99%</p>
                    <p className="text-gray-700 font-medium">UV Ray Protection</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews CTA */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Rated 5 Stars by Our Customers</h2>
          <p className="text-gray-600 mb-6">See what our customers are saying about us on Google</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild variant="outline" className="border-2 border-gray-300 hover:border-red-600 hover:text-red-600">
              <a href="https://www.google.com/maps/place/Texas+Tint+Studio" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Read Our Google Reviews
              </a>
            </Button>
            <Button asChild className="bg-red-600 hover:bg-red-700 text-white">
              <Link to="/reviews">See All Reviews</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg sm:text-xl text-red-100 mb-6 sm:mb-8 leading-relaxed">
            Book your appointment today. Professional service, competitive pricing, and guaranteed satisfaction.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg">
              <Link to="/book">Book Appointment</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg rounded-lg transition-all duration-200">
              <a href="tel:(713)446-8928">Call (713) 446-8928</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
